import React, { useState, useMemo } from 'react';
import NavigationHeader from '../components/NavigationHeader';
import Footer from '../components/Footer';
import HowToBook from '../components/HowToBook';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2, UserPlus, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import { pricingData, startDates } from '../siteData';

const WEBHOOK_URL = 'https://auto.ertheo.biz/webhook/997033b4-4712-448e-b8b8-4ccce2e2f248';
const CAMP_NAME = 'Oakham-Football-Camps';

const SignUpPage = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const [formData, setFormData] = useState({
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    playerName: '',
    playerAge: '',
    playerGender: '',
    program: '',
    duration: '',
    startDate: '',
    medicalInfo: '',
    dietaryRequirements: '',
    emergencyContact: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  // Get duration options based on selected program
  const durationOptions = useMemo(() => {
    if (!formData.program) return [];
    const programKey = formData.program === 'clinic' ? 'clinic' : 'astonVilla';
    return pricingData[programKey] || [];
  }, [formData.program]);

  // Get start date options based on selected program
  const dateOptions = useMemo(() => {
    if (!formData.program) return [];
    return startDates.filter(date => 
      date.programs.includes(formData.program) && date.available
    );
  }, [formData.program]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const payload = {
        ...formData,
        // Hidden fields
        language: language,
        camp_name: CAMP_NAME,
        form_type: 'signup',
        submitted_at: new Date().toISOString()
      };

      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          parentName: '',
          parentEmail: '',
          parentPhone: '',
          playerName: '',
          playerAge: '',
          playerGender: '',
          program: '',
          duration: '',
          startDate: '',
          medicalInfo: '',
          dietaryRequirements: '',
          emergencyContact: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If program changes, reset duration and startDate
    if (name === 'program') {
      setFormData({
        ...formData,
        program: value,
        duration: '',
        startDate: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <UserPlus className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-black mb-6">{t('signUp.heroTitle')}</h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            {t('signUp.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-10">
              {/* Parent/Guardian Information */}
              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-black mr-3">1</span>
                  {t('signUp.parentGuardianInfo')}
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="parentName" className="font-bold">{t('signUp.fullName')} *</Label>
                    <Input 
                      id="parentName"
                      name="parentName"
                      value={formData.parentName}
                      onChange={handleChange}
                      placeholder="John Smith"
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="parentEmail" className="font-bold">{t('signUp.emailAddress')} *</Label>
                    <Input 
                      id="parentEmail"
                      name="parentEmail"
                      type="email"
                      value={formData.parentEmail}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="mt-2"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="parentPhone" className="font-bold">{t('signUp.phoneNumber')} *</Label>
                    <Input 
                      id="parentPhone"
                      name="parentPhone"
                      type="tel"
                      value={formData.parentPhone}
                      onChange={handleChange}
                      placeholder="+44 123 456 7890"
                      required
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>

              {/* Player Information */}
              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-black mr-3">2</span>
                  {t('signUp.playerInfo')}
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                    <Label htmlFor="playerName" className="font-bold">{t('signUp.playerFullName')} *</Label>
                    <Input 
                      id="playerName"
                      name="playerName"
                      value={formData.playerName}
                      onChange={handleChange}
                      placeholder="James Smith"
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="playerAge" className="font-bold">{t('signUp.age')} *</Label>
                    <Input 
                      id="playerAge"
                      name="playerAge"
                      type="number"
                      min="10"
                      max="17"
                      value={formData.playerAge}
                      onChange={handleChange}
                      placeholder="14"
                      required
                      className="mt-2"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Label htmlFor="playerGender" className="font-bold">{t('signUp.gender')} *</Label>
                    <select
                      id="playerGender"
                      name="playerGender"
                      value={formData.playerGender}
                      onChange={handleChange}
                      required
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">{t('signUp.selectGender')}</option>
                      <option value="male">{t('signUp.male')}</option>
                      <option value="female">{t('signUp.female')}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Program Selection */}
              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-black mr-3">3</span>
                  {t('signUp.programSelection')}
                </h2>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="md:col-span-3">
                    <Label htmlFor="program" className="font-bold">{t('signUp.program')} *</Label>
                    <select
                      id="program"
                      name="program"
                      value={formData.program}
                      onChange={handleChange}
                      required
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">{t('signUp.selectProgram')}</option>
                      <option value="clinic">{t('signUp.highPerformanceClinic')}</option>
                      <option value="aston-villa">{t('signUp.astonVillaProgram')}</option>
                    </select>
                  </div>
                  <div className="md:col-span-3">
                    <Label htmlFor="duration" className="font-bold">{t('signUp.duration')} *</Label>
                    <div className="mt-2 grid gap-3">
                      {!formData.program ? (
                        <p className="text-gray-500 text-sm py-2">{t('signUp.selectProgramFirst') || 'Select a program first'}</p>
                      ) : (
                        durationOptions.map((option, index) => (
                          <label
                            key={index}
                            className={`relative flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              formData.duration === option.duration.toLowerCase().replace(' ', '-')
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="radio"
                                name="duration"
                                value={option.duration.toLowerCase().replace(' ', '-')}
                                checked={formData.duration === option.duration.toLowerCase().replace(' ', '-')}
                                onChange={handleChange}
                                className="h-5 w-5 text-green-600 focus:ring-green-500"
                              />
                              <div>
                                <span className="font-bold text-gray-900">{option.duration}</span>
                                {option.popular && (
                                  <span className="ml-2 px-2 py-0.5 bg-green-600 text-white text-xs font-bold rounded-full">
                                    {language === 'es' ? 'Popular' : language === 'fr' ? 'Populaire' : 'Popular'}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-xl font-black text-green-600">{option.price}</span>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-3">
                    <Label htmlFor="startDate" className="font-bold">{t('signUp.startDate')} *</Label>
                    <select
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      required
                      disabled={!formData.program}
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">{formData.program ? t('signUp.selectStartDate') : t('signUp.selectProgramFirst') || 'Select a program first'}</option>
                      {dateOptions.map((option, index) => (
                        <option key={index} value={option.date}>
                          {option.date}{option.spotsLeft <= 10 ? ` (${option.spotsLeft} ${language === 'es' ? 'plazas' : language === 'fr' ? 'places' : 'spots left'})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-black mr-3">4</span>
                  {t('signUp.additionalInfo')}
                </h2>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="medicalInfo" className="font-bold">{t('signUp.medicalConditions')}</Label>
                    <Input 
                      id="medicalInfo"
                      name="medicalInfo"
                      value={formData.medicalInfo}
                      onChange={handleChange}
                      placeholder={t('signUp.medicalPlaceholder')}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dietaryRequirements" className="font-bold">{t('signUp.dietaryRequirements')}</Label>
                    <Input 
                      id="dietaryRequirements"
                      name="dietaryRequirements"
                      value={formData.dietaryRequirements}
                      onChange={handleChange}
                      placeholder={t('signUp.dietaryPlaceholder')}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="emergencyContact" className="font-bold">{t('signUp.emergencyContact')} *</Label>
                    <Input 
                      id="emergencyContact"
                      name="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={handleChange}
                      placeholder={t('signUp.emergencyPlaceholder')}
                      required
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="border-t pt-8">
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md mb-6" data-testid="signup-success-message">
                    {t('signUp.successMessage') || 'Thank you for registering! We will contact you shortly with confirmation details.'}
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md mb-6" data-testid="signup-error-message">
                    {t('signUp.errorMessage') || 'There was an error processing your registration. Please try again.'}
                  </div>
                )}

                <div className="bg-green-50 border-l-4 border-green-600 p-6 mb-6">
                  <div className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-green-600 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="font-black text-gray-900 mb-2">{t('signUp.whatHappensNext')}</h3>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• {t('signUp.step1')}</li>
                        <li>• {t('signUp.step2')}</li>
                        <li>• {t('signUp.step3')}</li>
                        <li>• {t('signUp.step4')}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white font-bold w-full text-lg py-7"
                  disabled={isSubmitting}
                  data-testid="signup-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {t('signUp.submitting') || 'Submitting...'}
                    </>
                  ) : (
                    t('signUp.completeRegistration')
                  )}
                </Button>

                <p className="text-xs text-gray-600 text-center mt-4">
                  {t('signUp.termsAgreement')}
                </p>
              </div>
            </form>
          </Card>
        </div>
      </section>

      <HowToBook />

      <Footer />
    </div>
  );
};

export default SignUpPage;
